import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Copy, Check, ExternalLink, Download, AlertTriangle } from 'lucide-react';
import { useApiKey } from '@/stores/apiKey.store';
import { showToast } from '@/lib/toast';
import CodeBlock from '../code/CodeBlock';

interface Props {
    open: boolean;
    onClose: () => void;
}

const MCP_URL = 'https://mcp.satutim.id/';

export default function McpConfigModal({ open, onClose }: Props) {
    const { key } = useApiKey();
    const [copied, setCopied] = useState(false);

    if (!open) return null;

    const apiKey = key || '<YOUR_SATUTIM_API_KEY>';
    const hasRealKey = Boolean(key);

    // Standard claude_desktop_config.json / continue config.json shape — the
    // "mcpServers" envelope. Different from Cursor (server entry only) and
    // VS Code (own top-level shape with type=http).
    const claudeConfig = {
        mcpServers: {
            satutim: {
                url: MCP_URL,
                headers: { Authorization: `Bearer ${apiKey}` },
            },
        },
    };

    const code = JSON.stringify(claudeConfig, null, 2);

    // Cursor deeplink: base64(JSON of server entry, no mcpServers wrapper).
    // Format documented at cursor.com/docs/context/mcp/install-links.
    const cursorConfig = {
        url: MCP_URL,
        headers: { Authorization: `Bearer ${apiKey}` },
    };
    const cursorB64 = typeof window !== 'undefined'
        ? window.btoa(JSON.stringify(cursorConfig))
        : '';
    const cursorHref = `cursor://anysphere.cursor-deeplink/mcp/install?name=satutim&config=${cursorB64}`;

    // VS Code MCP install link (1.94+): vscode:mcp/install?<URL-encoded JSON>.
    // type=http for streamable HTTP transport per MCP spec.
    const vscodeConfig = {
        name: 'satutim',
        type: 'http',
        url: MCP_URL,
        headers: { Authorization: `Bearer ${apiKey}` },
    };
    const vscodeHref = `vscode:mcp/install?${encodeURIComponent(JSON.stringify(vscodeConfig))}`;

    const onCopy = async () => {
        let ok = false;
        try {
            await navigator.clipboard.writeText(code);
            ok = true;
        } catch {
            try {
                const ta = document.createElement('textarea');
                ta.value = code;
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.select();
                ok = document.execCommand('copy');
                ta.remove();
            } catch { /* both methods failed */ }
        }
        if (ok) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
            showToast('Config tersalin');
        } else {
            showToast('Gagal menyalin — pilih config-nya manual lalu Ctrl/Cmd+C');
        }
    };

    const onDownload = () => {
        try {
            const blob = new Blob([code], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'claude_desktop_config.json';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            showToast('claude_desktop_config.json di-download');
        } catch {
            showToast('Gagal download — pakai Salin config sebagai gantinya');
        }
    };

    const warnNoKey = () => {
        if (!hasRealKey) {
            showToast('Generate API key dulu sebelum import — placeholder gak akan bisa auth');
            return false;
        }
        return true;
    };

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
            onClick={onClose}
        >
            <div
                className="w-full max-w-2xl rounded-xl overflow-hidden max-h-[90vh] flex flex-col"
                style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    className="flex items-center justify-between px-5 py-3 shrink-0"
                    style={{ borderBottom: '1px solid var(--border)' }}
                >
                    <h2 className="text-base font-semibold text-ink">Setup MCP Server</h2>
                    <button onClick={onClose} className="p-1 rounded hover:bg-bg-hover text-muted">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="p-5 space-y-5 overflow-y-auto">
                    <p className="text-sm text-muted leading-relaxed">
                        SatuTim hosting MCP server-nya di <code className="text-xs px-1.5 py-0.5 rounded font-mono" style={{ backgroundColor: 'var(--surface-1)', color: 'var(--accent)' }}>mcp.satutim.id</code>.
                        Tidak butuh install apa-apa di komputer — langsung connect.
                    </p>

                    {/* Quick-install buttons */}
                    <div>
                        <h3 className="text-xs font-semibold text-ink uppercase tracking-wider mb-2">
                            Import langsung
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <a
                                href={cursorHref}
                                onClick={(e) => { if (!warnNoKey()) e.preventDefault(); }}
                                className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-md text-xs font-medium text-ink hover:brightness-125 transition-all"
                                style={{ backgroundColor: 'var(--surface-1)', border: '1px solid var(--border)' }}
                            >
                                <span>Add to Cursor</span>
                                <ExternalLink className="h-3 w-3 text-ghost" />
                            </a>
                            <a
                                href={vscodeHref}
                                onClick={(e) => { if (!warnNoKey()) e.preventDefault(); }}
                                className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-md text-xs font-medium text-ink hover:brightness-125 transition-all"
                                style={{ backgroundColor: 'var(--surface-1)', border: '1px solid var(--border)' }}
                            >
                                <span>Add to VS Code</span>
                                <ExternalLink className="h-3 w-3 text-ghost" />
                            </a>
                            <button
                                onClick={() => { if (warnNoKey()) onDownload(); }}
                                className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-md text-xs font-medium text-ink hover:brightness-125 transition-all text-left"
                                style={{ backgroundColor: 'var(--surface-1)', border: '1px solid var(--border)' }}
                            >
                                <span>Claude Desktop</span>
                                <Download className="h-3 w-3 text-ghost" />
                            </button>
                        </div>
                        {!hasRealKey && (
                            <p className="text-xxs mt-2" style={{ color: 'var(--warn)' }}>
                                Login dengan akun SatuTim atau aktifkan demo mode dulu — tombol import butuh API key valid.
                            </p>
                        )}

                        {/* Claude.ai web — known broken on Anthropic's side */}
                        <div
                            className="mt-3 flex items-start gap-2.5 px-3 py-2.5 rounded-md text-xxs"
                            style={{ backgroundColor: 'var(--warn-bg)', border: '1px solid var(--warn-border)', color: 'var(--warn)' }}
                        >
                            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                            <div className="flex-1 leading-relaxed">
                                <div className="font-semibold mb-0.5">Claude.ai web — belum jalan</div>
                                <div style={{ color: 'var(--warn-muted)' }}>
                                    Server kami sudah OAuth 2.1 + PKCE compliant, tapi Claude.ai broker
                                    tidak mengirim bearer token ke MCP server setelah OAuth — bug yang
                                    diakui Anthropic di{' '}
                                    <a
                                        href="https://github.com/anthropics/claude-ai-mcp/issues/171"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="underline hover:no-underline"
                                    >
                                        issue #171
                                    </a>. Pakai Claude Desktop di atas untuk sementara.
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Manual config block */}
                    <div>
                        <h3 className="text-xs font-semibold text-ink uppercase tracking-wider mb-2">
                            Atau copy manual
                        </h3>
                        <CodeBlock code={code} lang="json" />
                        <ul className="space-y-1.5 text-xs text-muted mt-3">
                            <li>
                                <strong className="text-ink">Claude Desktop</strong> —{' '}
                                <code className="text-xxs px-1 py-0.5 rounded font-mono" style={{ backgroundColor: 'var(--surface-1)' }}>
                                    ~/Library/Application Support/Claude/claude_desktop_config.json
                                </code>
                            </li>
                            <li>
                                <strong className="text-ink">Continue.dev</strong> —{' '}
                                <code className="text-xxs px-1 py-0.5 rounded font-mono" style={{ backgroundColor: 'var(--surface-1)' }}>
                                    ~/.continue/config.json
                                </code> → field <code className="text-xxs">mcpServers</code>
                            </li>
                            <li>
                                <strong className="text-ink">ChatGPT</strong> — belum support MCP native. Pakai GPT Actions terpisah kalau perlu integrasi.
                            </li>
                        </ul>
                    </div>

                    <div className="flex items-center justify-between gap-3 pt-2">
                        <div className="flex items-center gap-3">
                            <a
                                href="https://mcp.satutim.id/manifest.json"
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-xxs text-ghost hover:text-muted transition-colors"
                            >
                                Lihat manifest <ExternalLink className="h-3 w-3" />
                            </a>
                            <a
                                href="https://inspector.modelcontextprotocol.io/?url=https://mcp.satutim.id/"
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-xxs text-ghost hover:text-muted transition-colors"
                            >
                                Test koneksi <ExternalLink className="h-3 w-3" />
                            </a>
                        </div>
                        <button
                            onClick={onCopy}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors"
                            style={{ backgroundColor: 'var(--accent)', color: 'var(--bg)' }}
                        >
                            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                            {copied ? 'Tersalin' : 'Salin config'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}
